// @purpose STATE
// Offline answer queue (S5.5, STACK "small JS offline queue in IndexedDB"). An answer that cannot
// reach the server is stored here, keyed `item:attempt` (the server-side idempotency key is
// `test:item:attempt`, so re-sending one is always safe), and flushed IN ORDER on reconnect. Falls
// back to an in-memory list when IndexedDB is unavailable (private mode), so nothing throws.
const DB_NAME = "calm-test";
const STORE = "answers";
const memory = new Map();
let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    try {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: "key" });
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return dbPromise;
}

function run(db, mode, fn) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const out = fn(tx.objectStore(STORE));
    tx.oncomplete = () => resolve(out && "result" in out ? out.result : undefined);
    tx.onerror = () => reject(tx.error);
  });
}

export async function enqueue(entry) {
  const row = { ...entry, at: entry.at || Date.now() };
  const db = await openDb();
  if (!db) {
    memory.set(row.key, row);
    return;
  }
  await run(db, "readwrite", (s) => s.put(row));
}

export async function pending() {
  const db = await openDb();
  const rows = db ? (await run(db, "readonly", (s) => s.getAll())) || [] : [...memory.values()];
  return rows.sort((a, b) => a.at - b.at);
}

export async function remove(key) {
  const db = await openDb();
  if (!db) {
    memory.delete(key);
    return;
  }
  await run(db, "readwrite", (s) => s.delete(key));
}

let flushing = null;

// send(entry) -> Promise<Response|null>; null (network down / 5xx) stops the flush, keeping order.
// Returns the last successful Response so the caller can show where the run is now.
export function flush(send) {
  if (flushing) return flushing;
  flushing = (async () => {
    let last = null;
    for (const entry of await pending()) {
      const res = await send(entry);
      if (!res) break;
      await remove(entry.key);
      last = res;
    }
    return last;
  })().finally(() => {
    flushing = null;
  });
  return flushing;
}
